/**
 * Undo/redo stack for splat edit operations.
 */
import type { EditOp } from './ops';

export class EditHistory {
    private undoStack: EditOp[] = [];
    private redoStack: EditOp[] = [];
    /** Bumped on every change so callers can tell when the document is dirty. */
    revision = 0;
    private savedRevision = 0;
    maxSize: number;

    onChange?: () => void;

    constructor(maxSize = 200) {
        this.maxSize = maxSize;
    }

    get canUndo(): boolean { return this.undoStack.length > 0; }
    get canRedo(): boolean { return this.redoStack.length > 0; }
    get undoName(): string | null { return this.undoStack[this.undoStack.length - 1]?.name ?? null; }
    get redoName(): string | null { return this.redoStack[this.redoStack.length - 1]?.name ?? null; }
    get length(): number { return this.undoStack.length; }

    /** Whether anything changed since the last markSaved(). */
    get dirty(): boolean { return this.revision !== this.savedRevision; }

    markSaved(): void {
        this.savedRevision = this.revision;
    }

    /** Execute an op and record it. Ops reporting `empty` are skipped. */
    add(op: EditOp & { empty?: boolean }): boolean {
        if (op.empty) return false;
        op.do();
        for (const r of this.redoStack) r.destroy?.();
        this.redoStack.length = 0;
        this.undoStack.push(op);
        while (this.undoStack.length > this.maxSize) this.undoStack.shift()?.destroy?.();
        this.revision++;
        this.onChange?.();
        return true;
    }

    undo(): EditOp | null {
        const op = this.undoStack.pop();
        if (!op) return null;
        op.undo();
        this.redoStack.push(op);
        this.revision++;
        this.onChange?.();
        return op;
    }

    redo(): EditOp | null {
        const op = this.redoStack.pop();
        if (!op) return null;
        op.do();
        this.undoStack.push(op);
        this.revision++;
        this.onChange?.();
        return op;
    }

    clear(): void {
        for (const op of this.undoStack) op.destroy?.();
        for (const op of this.redoStack) op.destroy?.();
        this.undoStack.length = 0;
        this.redoStack.length = 0;
        this.revision = 0;
        this.savedRevision = 0;
        this.onChange?.();
    }
}
